import React, { useCallback, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ContentOverviewCard,
  ContentOverviewCardState,
  Button,
  formatMinutes,
  ButtonAppearance,
  ContentType,
} from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { N800 } from '@atlaskit/theme/colors';

import { User } from 'src/types';
import { getInspectModeData } from 'src/utils/contentful';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { LearningProgressStatus } from 'src/services/http/progress';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { sendTrackEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { useLessonProgress } from 'src/store/progress/progressStore';

interface LessonListProps {
  course: ContentfulSmCourse | null;
  user?: User | null;
  numLessonsShown?: number;
  isLoading?: boolean;
}

export const MAX_LESSONS_SHOWN = 6;

export const OldLessonList: React.FC<LessonListProps> = ({
  course,
  user,
  numLessonsShown = MAX_LESSONS_SHOWN,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [shouldShowAllLessons, setShouldShowAllLessons] = useState<boolean>(false);
  const [{ progressForLessons, isLoadingProgressForLessons }] = useLessonProgress();
  const getLessonState = (lessonId: string): ContentOverviewCardState => {
    if (isLoading || isLoadingProgressForLessons) {
      return ContentOverviewCardState.LOADING;
    }

    if (!progressForLessons || !user) {
      return ContentOverviewCardState.DEFAULT;
    }

    const lessonProgress = progressForLessons.lessons.find((p) => p.lessonId === lessonId);

    if (lessonProgress?.status === LearningProgressStatus.IN_PROGRESS) {
      return ContentOverviewCardState.IN_PROGRESS;
    }

    if (lessonProgress?.status === LearningProgressStatus.COMPLETED) {
      return ContentOverviewCardState.FINISHED;
    }

    return ContentOverviewCardState.DEFAULT;
  };

  const handleHrefLink = useCallback(
    (lesson: ContentfulSmCourse['lessonsCollection']['items'][0]): string => {
      if (!course) {
        return '';
      }

      const courseSlug = getSlug(course);
      const baseUrl = getLessonUrl({
        lessonSlug: getSlug(lesson),
        courseSlug,
        pathSlug: params?.pathId,
      });

      const hubPart = getLearningPathsHubPart(location.pathname);
      const url = hubPart ? `${hubPart}${baseUrl}` : baseUrl;

      return withPrefix(url);
    },
    [course, params?.pathId, location.pathname],
  );

  const onCardClick = useCallback(
    (lesson: ContentfulSmCourse['lessonsCollection']['items'][0]): void => {
      if (course) {
        const courseSlug = getSlug(course);

        const url = withPrefix(
          getLessonUrl({
            lessonSlug: getSlug(lesson),
            courseSlug,
            pathSlug: params?.pathId,
          }),
          getLearningPathsHubPart(location.pathname),
        );

        const hasNotStartedCourse =
          !progressForLessons?.overallStatus || progressForLessons.overallStatus === LearningProgressStatus.NOT_STARTED;

        if (hasNotStartedCourse) {
          sendTrackEvent(
            'learningApp',
            'course',
            'started',
            {
              courseSlug,
            },
            user?.account_id,
          );
        }

        navigate(url);
      }
    },
    [course, navigate, params?.pathId, location.pathname, progressForLessons, user],
  );

  const showAllLessons = useCallback(() => {
    setShouldShowAllLessons(true);
  }, [setShouldShowAllLessons]);

  if (!course?.lessonsCollection.items || course.lessonsCollection.items.length === 0) {
    return null;
  }

  const lessonsToShow = shouldShowAllLessons
    ? course.lessonsCollection.items
    : course.lessonsCollection.items.slice(0, numLessonsShown);

  return (
    <section data-testid="lesson-list" css={sectionStyles}>
      <div css={containerStyles}>
        <p data-testid="lesson-quantity-title" css={headerStyles}>
          {course.lessonsCollection.items.length}{' '}
          {`${course.lessonsCollection.items.length > 1 ? 'Lessons' : 'Lesson'} in this course`}
        </p>
        {lessonsToShow.map((lesson) => (
          <ContentOverviewCard
            inspectMode={getInspectModeData(lesson, ['title', 'description', 'sectionList'])}
            key={lesson.sys.id} // to support duplicated lessons for testing purposes
            tags={[{ label: formatMinutes(lesson.duration), inspectMode: getInspectModeData(lesson, ['duration']) }]}
            heading={lesson.title}
            subheading={lesson?.teaser || lesson.description}
            collapsedSectionTitle="Sections"
            collapsedSectionItems={lesson.sectionListCollection.items
              .filter((section) => section.title)
              .map((section) => section.title)}
            state={getLessonState(lesson.sys.id)}
            onClickButton={() => onCardClick(lesson)}
            contentType={ContentType.COURSE}
            href={handleHrefLink(lesson)}
          />
        ))}
      </div>
      <div css={css({ marginTop: '1rem' })}>
        {lessonsToShow.length < course.lessonsCollection.items.length && (
          <Button label="See All Lessons" appearance={ButtonAppearance.LINK} onClick={showAllLessons} />
        )}
      </div>
    </section>
  );
};

const sectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  rowGap: '1rem',
  marginTop: '1.5rem',
  width: '100%',
});

const containerStyles = css({
  width: '100%',
  gap: '1rem',
  display: 'flex',
  flexDirection: 'column',
});

const headerStyles = css({
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '1.5rem',
  lineHeight: '28px',
  fontWeight: 600,
  color: N800,
  marginTop: 0,
  marginBottom: '1rem',
});
